import { useCallback, useEffect, useRef, useState } from 'react';

interface Options {
  /** Idle time before the warning appears. */
  inactivityTimeout: number;
  /** Countdown shown in the warning before `onTimeout` fires. */
  countdownTimeout?: number;
  enabled: boolean;
  onTimeout: () => void;
}

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

/**
 * Idle detection ported from the production client (use-inactivity-tracker.ts): after
 * `inactivityTimeout` without activity a warning opens with a countdown; activity no longer resets
 * the timer while it is open — only `stayActive()` does.
 */
export function useInactivityTracker({
  inactivityTimeout,
  countdownTimeout = 10_000,
  enabled,
  onTimeout,
}: Options) {
  const initialSeconds = Math.floor(countdownTimeout / 1000);
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);
  const warningRef = useRef(false);
  const idleTimer = useRef<number | null>(null);
  const countdownTimer = useRef<number | null>(null);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const clearTimers = useCallback(() => {
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    if (countdownTimer.current) window.clearInterval(countdownTimer.current);
    idleTimer.current = null;
    countdownTimer.current = null;
  }, []);

  const arm = useCallback(() => {
    clearTimers();
    warningRef.current = false;
    setWarning(false);
    setSecondsLeft(initialSeconds);
    idleTimer.current = window.setTimeout(() => {
      warningRef.current = true;
      setWarning(true);
      let remaining = initialSeconds;
      setSecondsLeft(remaining);
      countdownTimer.current = window.setInterval(() => {
        remaining = Math.max(0, remaining - 1);
        setSecondsLeft(remaining);
        if (remaining <= 0) {
          clearTimers();
          onTimeoutRef.current();
        }
      }, 1000);
    }, inactivityTimeout);
  }, [clearTimers, inactivityTimeout, initialSeconds]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      warningRef.current = false;
      setWarning(false);
      return;
    }
    arm();
    const onActivity = () => {
      if (!warningRef.current) arm();
    };
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, onActivity, { passive: true });
    return () => {
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, onActivity);
      clearTimers();
    };
  }, [enabled, arm, clearTimers]);

  return { warning, secondsLeft, totalSeconds: initialSeconds, stayActive: arm };
}
